import { AgentPlatformSurface, useAgentProfiles } from '@commenda/api';
import { Button, FieldWrapper, Label, useToggle } from '@commenda/kakashi';
import { AgentProfileHelper } from '@commenda/utils';
import { useMemo } from 'react';

import { AgentProfilePickerModal } from './AgentProfilePickerModal';

interface AgentProfileFieldProps {
  profileKey: string | undefined;
  onChange: (profileKey: string | undefined) => void;
  enabled: boolean;
}

export const AgentProfileField = ({
  profileKey,
  onChange,
  enabled,
}: AgentProfileFieldProps) => {
  const profilePickerModalVisibility = useToggle(false);
  // Uppercase: the query DTO validates with @IsEnum and has no transform.
  const { profiles, error, isLoading, mutate } = useAgentProfiles({
    surface: AgentPlatformSurface.WORKFLOW_NODE,
    enabled,
  });

  const availableProfiles = useMemo(() => profiles ?? [], [profiles]);
  const selectedProfile = availableProfiles.find(
    (profile) => profile.profileKey === profileKey
  );
  // A saved key stays visible even when the profile no longer offers this surface: publishing will
  // refuse it, and silently blanking the field would hide that.
  const isStranded = Boolean(profileKey) && !selectedProfile && !isLoading;

  return (
    <FieldWrapper>
      <Label optional>Runs as</Label>

      {selectedProfile ? (
        <div className="flex flex-col gap-xs">
          <span className="txt-small-medium text-text-primary">
            {AgentProfileHelper.getDisplayName(selectedProfile)}
          </span>
          <p className="txt-extra-small-regular line-clamp-3 text-text-secondary">
            {selectedProfile.description}
          </p>
        </div>
      ) : (
        <p className="txt-extra-small-regular text-text-secondary">
          {isStranded
            ? `This step names ${profileKey}.`
            : 'Runs as the built-in workflow-node profile.'}
        </p>
      )}

      {isStranded && !error ? (
        <p className="txt-extra-small-regular text-text-error mt-xs">
          That expert no longer runs on a workflow node, so publishing this
          workflow will refuse it. Choose one that does, or clear it.
        </p>
      ) : null}

      {error ? (
        <div className="mt-xs flex items-center gap-sm">
          <span className="txt-extra-small-regular text-text-error">
            The experts could not be loaded.
          </span>
          <Button variant="tertiary" size="xs" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      ) : (
        <Button
          variant="tertiary"
          size="sm"
          className="mt-xs self-start"
          disabled={isLoading}
          onClick={profilePickerModalVisibility.on}
        >
          {selectedProfile ? 'Change expert' : 'Choose an expert'}
        </Button>
      )}

      {profilePickerModalVisibility.value ? (
        <AgentProfilePickerModal
          profiles={availableProfiles}
          selectedProfileKey={profileKey}
          onClose={profilePickerModalVisibility.off}
          onSelect={onChange}
        />
      ) : null}
    </FieldWrapper>
  );
};
